import type { ApiErrorBody } from "./apiError";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /** Server log reference, present only on sanitized server-side failures. */
    public ref?: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function api<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });

  if (!res.ok) {
    // Two kinds of error body, told apart by shape -- see apiError.ts. A JSON
    // body has already been sanitized by the server and carries a log reference.
    // A plain-text body is a message this codebase wrote about the request
    // ("start and end are required", "Unauthorized"): safe to show, and the
    // reason the user can fix it themselves.
    const text = await res.text();
    try {
      const parsed = JSON.parse(text) as Partial<ApiErrorBody>;
      if (typeof parsed?.error === "string") {
        throw new ApiError(res.status, parsed.error, parsed.ref);
      }
    } catch (err) {
      if (err instanceof ApiError) throw err;
      // Not JSON. Fall through and use the text as written.
    }
    throw new ApiError(res.status, text);
  }

  return res.json() as Promise<T>;
}
